/**
 * SettingsSEOScore — real-time SEO scoring widget.
 *
 * Analyzes a page's SEO fields and provides an aggregate score (0-100) with
 * specific actionable recommendations. Similar to Yoast / RankMath / Ahrefs
 * on-page SEO checkers.
 */
import React, { useMemo } from 'react';
import { Check, AlertTriangle, X, TrendingUp } from 'lucide-react';

// ── Individual SEO checks ────────────────────────────────────────────────
const checks = [
  {
    id: 'title',
    label: 'Page title length (30–60 characters)',
    run: (seo) => {
      const t = (seo.title || '').trim();
      if (!t) return { ok: false, msg: 'Add a page title — this shows in Google results.' };
      if (t.length < 30) return { ok: false, msg: `Title is too short (${t.length} chars). Aim for 30–60.` };
      if (t.length > 60) return { ok: 'warn', msg: `Title may be truncated in search results (${t.length} chars).` };
      return { ok: true };
    },
  },
  {
    id: 'description',
    label: 'Meta description (120–160 characters)',
    run: (seo) => {
      const d = (seo.description || seo.meta_description || '').trim();
      if (!d) return { ok: false, msg: 'Add a meta description — improves click-through from search.' };
      if (d.length < 120) return { ok: false, msg: `Description too short (${d.length} chars). Aim for 120–160.` };
      if (d.length > 160) return { ok: 'warn', msg: `Description may be truncated (${d.length} chars).` };
      return { ok: true };
    },
  },
  {
    id: 'keywords',
    label: 'Focus keywords defined',
    run: (seo) => {
      const k = seo.keywords || seo.focus_keywords || '';
      if (!k || (typeof k === 'string' && k.trim().length < 3)) return { ok: 'warn', msg: 'Add focus keywords to help search engines understand your page.' };
      return { ok: true };
    },
  },
  {
    id: 'og_image',
    label: 'Social share image (Open Graph)',
    run: (seo) => {
      if (!seo.og_image && !seo.social_image) return { ok: false, msg: 'Upload an Open Graph image — shows when your link is shared on social media.' };
      return { ok: true };
    },
  },
  {
    id: 'favicon',
    label: 'Favicon uploaded',
    run: (seo, settings) => {
      if (!settings?.favicon_url) return { ok: 'warn', msg: 'Upload a favicon to appear in browser tabs.' };
      return { ok: true };
    },
  },
  {
    id: 'sitemap',
    label: 'Sitemap auto-generated',
    run: () => ({ ok: true }),
  },
  {
    id: 'ssl',
    label: 'HTTPS / SSL enabled',
    run: () => ({ ok: true }),
  },
  {
    id: 'mobile',
    label: 'Mobile-responsive design',
    run: () => ({ ok: true }),
  },
];

export default function SettingsSEOScore({ seo = {}, settings = {} }) {
  const results = useMemo(() => checks.map((c) => ({ ...c, result: c.run(seo, settings) })), [seo, settings]);
  const passed = results.filter((r) => r.result.ok === true).length;
  const warned = results.filter((r) => r.result.ok === 'warn').length;
  const failed = results.filter((r) => r.result.ok === false).length;
  const total = results.length;
  const score = Math.round(((passed * 100 + warned * 60) / total));
  const scoreColor = score >= 80 ? 'emerald' : score >= 60 ? 'amber' : 'rose';

  const gradient = {
    emerald: 'from-emerald-400 to-cyan-400',
    amber: 'from-amber-400 to-orange-400',
    rose: 'from-rose-400 to-pink-400',
  }[scoreColor];

  return (
    <div className="rounded-2xl border border-white/10 bg-[#15152E] p-5" data-testid="seo-score-widget">
      <div className="flex items-center gap-4 flex-wrap">
        {/* Circular score */}
        <div className="relative w-24 h-24 shrink-0">
          <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
            <circle cx="50" cy="50" r="42" stroke="rgba(255,255,255,0.08)" strokeWidth="8" fill="none" />
            <circle
              cx="50" cy="50" r="42"
              stroke={`url(#seo-grad-${scoreColor})`}
              strokeWidth="8"
              fill="none"
              strokeLinecap="round"
              strokeDasharray={`${(score / 100) * 264} 264`}
              className="transition-all duration-700"
            />
            <defs>
              <linearGradient id={`seo-grad-${scoreColor}`} x1="0" y1="0" x2="100" y2="100">
                <stop offset="0%" stopColor={scoreColor === 'emerald' ? '#34D399' : scoreColor === 'amber' ? '#FBBF24' : '#FB7185'} />
                <stop offset="100%" stopColor={scoreColor === 'emerald' ? '#22D3EE' : scoreColor === 'amber' ? '#FB923C' : '#EC4899'} />
              </linearGradient>
            </defs>
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-2xl font-bold text-white" data-testid="seo-score-value">{score}</span>
            <span className="text-[9px] uppercase tracking-wider text-white/50">/ 100</span>
          </div>
        </div>
        <div className="flex-1 min-w-[200px]">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp size={14} className={`text-${scoreColor}-300`} />
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/60">SEO Score</p>
          </div>
          <p className="text-white font-bold text-lg">
            {score >= 80 ? 'Excellent SEO' : score >= 60 ? 'Good — Room to improve' : 'Needs Work'}
          </p>
          <p className="text-xs text-white/55 mt-1">
            {passed} passed · {warned} warnings · {failed} issues
          </p>
        </div>
      </div>

      <div className="mt-5 space-y-1.5">
        {results.map((r) => (
          <div key={r.id} className="flex items-start gap-2.5 py-2 px-3 rounded-lg bg-white/[0.03]" data-testid={`seo-check-${r.id}`}>
            {r.result.ok === true && <Check size={14} className="text-emerald-400 mt-0.5 shrink-0" />}
            {r.result.ok === 'warn' && <AlertTriangle size={14} className="text-amber-400 mt-0.5 shrink-0" />}
            {r.result.ok === false && <X size={14} className="text-rose-400 mt-0.5 shrink-0" />}
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white/85">{r.label}</p>
              {r.result.msg && <p className="text-[11px] text-white/50 mt-0.5">{r.result.msg}</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
